// Contexto de carteiras com Firebase Firestore

import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { 
  getUserWallets, 
  addWallet as firebaseAddWallet,
  updateWallet as firebaseUpdateWallet,
  deleteWallet as firebaseDeleteWallet
} from '@/services/firebase';

const WalletContext = createContext();

export function WalletProvider({ children }) {
  const { user } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Carregar carteiras quando usuário está autenticado
  useEffect(() => {
    if (!user?.uid) {
      setWallets([]);
      setSelectedWallet(null);
      return;
    }

    const loadWallets = async () => {
      try {
        setLoading(true);
        setError(null);
        const userWallets = await getUserWallets(user.uid);
        setWallets(userWallets);
      } catch (err) {
        console.error('Erro ao carregar carteiras:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadWallets();
  }, [user?.uid]);

  const addWallet = async (walletData) => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }

    try {
      setError(null);
      const walletId = await firebaseAddWallet(user.uid, walletData);
      
      // Adicionar à lista local
      const newWallet = {
        id: walletId,
        ...walletData,
        createdAt: new Date().toISOString(),
      };
      setWallets([...wallets, newWallet]);
      return newWallet;
    } catch (err) {
      console.error('Erro ao adicionar carteira:', err);
      setError(err.message);
      throw err;
    }
  };

  const updateWallet = async (walletId, updates) => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }

    try {
      setError(null);
      await firebaseUpdateWallet(user.uid, walletId, updates);
      
      // Atualizar na lista local
      setWallets(wallets.map(w => 
        w.id === walletId ? { ...w, ...updates } : w
      ));
    } catch (err) {
      console.error('Erro ao atualizar carteira:', err);
      setError(err.message);
      throw err;
    }
  };

  const deleteWallet = async (walletId) => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }

    try {
      setError(null);
      await firebaseDeleteWallet(user.uid, walletId);
      
      // Remover da lista local
      setWallets(wallets.filter(w => w.id !== walletId));
      
      // Se a carteira deletada era a selecionada, desselecionar
      if (selectedWallet?.id === walletId) {
        setSelectedWallet(null);
      }
    } catch (err) {
      console.error('Erro ao deletar carteira:', err);
      setError(err.message);
      throw err;
    }
  };

  const getWallet = (walletId) => {
    return wallets.find(w => w.id === walletId);
  };

  const searchWallets = (query) => {
    const lowerQuery = query.toLowerCase();
    return wallets.filter(w => 
      w.name?.toLowerCase().includes(lowerQuery) ||
      w.address?.toLowerCase().includes(lowerQuery) ||
      w.email?.toLowerCase().includes(lowerQuery) ||
      w.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
    );
  };

  const exportWallets = () => {
    return JSON.stringify(wallets, null, 2);
  };

  const importWallets = async (data, merge = true) => {
    if (!user?.uid) return false;
    try {
      const raw = typeof data === 'string' ? data : JSON.stringify(data);
      const imported = JSON.parse(raw);
      const list = Array.isArray(imported) ? imported : (imported.wallets || imported.data || []);
      if (!list.length) return false;

      const toAdd = list.map((w) => {
        const tags = Array.isArray(w.tags) ? w.tags : (typeof w.tags === 'string' ? w.tags.split(',').map(t => t.trim()).filter(Boolean) : []);
        return {
          name: w.name || '',
          address: (w.address || '').trim(),
          network: w.network || 'ethereum',
          email: w.email?.trim() || null,
          phone: w.phone?.trim() || null,
          tags,
        };
      }).filter(w => w.address && w.name);

      if (!merge) {
        setWallets([]);
      }

      for (const walletData of toAdd) {
        await firebaseAddWallet(user.uid, walletData);
      }

      const updated = await getUserWallets(user.uid);
      setWallets(updated);
      return true;
    } catch (error) {
      console.error('Erro ao importar carteiras:', error);
      return false;
    }
  };

  return (
    <WalletContext.Provider value={{
      wallets,
      selectedWallet,
      loading,
      error,
      addWallet,
      updateWallet,
      deleteWallet,
      getWallet,
      setSelectedWallet,
      searchWallets,
      exportWallets,
      importWallets,
    }}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallets() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallets deve ser usado dentro de WalletProvider');
  }
  return context;
}
